import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Check,
  ChevronDown,
  Building2,
  User,
  Settings,
  LogOut,
  Search,
  RefreshCw,
  Plus,
  Smartphone,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';

export const ProfileSwitcher = () => {
  const { profiles, userProfile, company, switchProfile, signOut, can } = useAuth();
  const [switching, setSwitching] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const canAccessCompanySettings = can('company.settings');

  const handleSwitch = async (profileId) => {
    if (profileId === userProfile?.id || switching) return;

    setSwitching(true);
    try {
      toast.success('Switching profile...');
      const result = await switchProfile(profileId);
      if (result?.success && result?.navigateTo) {
        navigate(result.navigateTo);
      }
      setOpen(false);
    } catch (error) {
      console.error('Error switching profile:', error);
      toast.error('Failed to switch profile');
    } finally {
      setSwitching(false);
    }
  };

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
  };

  const getProfileLabel = (profile) => {
    const firstName = profile?.first_name || '';
    const lastName = profile?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim() || profile?.email || '';
    return fullName;
  };

  const getInitials = (profile) => {
    const firstName = profile?.first_name || '';
    const lastName = profile?.last_name || '';
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    return profile?.email?.[0]?.toUpperCase() || '?';
  };

  // Filter profiles based on search query
  const filteredProfiles = useMemo(() => {
    if (!profiles) return [];
    if (!searchQuery.trim()) return profiles;

    const query = searchQuery.toLowerCase();
    return profiles.filter(profile => {
      const name = getProfileLabel(profile).toLowerCase();
      const email = (profile.email || '').toLowerCase();
      const companyName = (profile.company?.name || '').toLowerCase();
      return name.includes(query) || email.includes(query) || companyName.includes(query);
    });
  }, [profiles, searchQuery]);

  // Determine if we should show the switcher section
  const hasMultipleProfiles = profiles && profiles.length > 1;
  // Show search when there are many profiles
  const showSearch = profiles && profiles.length > 4;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          disabled={switching}
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={userProfile?.avatar_url} />
            <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-white text-xs">
              {getInitials(userProfile)}
            </AvatarFallback>
          </Avatar>
          <div className="hidden sm:flex flex-col items-start">
            <span className="text-sm font-medium text-gray-900">
              {getProfileLabel(userProfile)}
            </span>
            {company && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                {company.name}
              </span>
            )}
          </div>
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''} ${switching ? 'animate-spin' : ''}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        {/* Current Profile Section */}
        <div className="p-4 border-b">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src={userProfile?.avatar_url} />
              <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-white text-sm">
                {getInitials(userProfile)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {getProfileLabel(userProfile)}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {userProfile?.email}
              </p>
              {company && (
                <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                  <Building2 className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{company.name}</span>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Settings Menu Section */}
        <div className="py-2">
          <Link
            to="/profile"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <User className="h-4 w-4 text-gray-500" />
            <span>My Profile</span>
          </Link>
          {canAccessCompanySettings && (
            <Link
              to="/company-settings"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <Building2 className="h-4 w-4 text-gray-500" />
              <span>Company Settings</span>
            </Link>
          )}
          <Link
            to="/settings"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <Settings className="h-4 w-4 text-gray-500" />
            <span>Account Settings</span>
          </Link>
          <Link
            to="/get-mobile-app"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <Smartphone className="h-4 w-4 text-gray-500" />
            <span>Get Mobile App</span>
          </Link>
          <Separator className="my-2" />
          <button
            onClick={handleSignOut}
            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors w-full text-left"
          >
            <LogOut className="h-4 w-4 text-gray-500" />
            <span>Sign Out</span>
          </button>
        </div>

        {/* Profile Switcher Section - Always shown but may have only one profile */}
        {hasMultipleProfiles && (
          <>
            <Separator />
            <div className="p-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-1">
                Switch Account
              </p>

              {/* Search input for many profiles */}
              {showSearch && (
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search accounts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-8 pl-8 text-sm"
                  />
                </div>
              )}

              {/* Scrollable profile list */}
              <ScrollArea className={profiles.length > 4 ? 'h-48' : ''}>
                <div className="space-y-1">
                  {filteredProfiles.map((profile) => {
                    const isActive = profile.id === userProfile?.id;

                    return (
                      <button
                        key={profile.id}
                        onClick={() => handleSwitch(profile.id)}
                        disabled={isActive || switching}
                        className={`flex items-center gap-3 w-full p-2 rounded-lg text-left transition-colors ${
                          isActive
                            ? 'bg-violet-50 cursor-default'
                            : 'hover:bg-gray-100 cursor-pointer'
                        } ${switching && !isActive ? 'opacity-50' : ''}`}
                      >
                        <Avatar className="h-9 w-9 flex-shrink-0">
                          <AvatarImage src={profile.avatar_url} />
                          <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-white text-xs">
                            {getInitials(profile)}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {getProfileLabel(profile)}
                            </span>
                            {isActive && (
                              <Check className="h-4 w-4 text-violet-600 flex-shrink-0" />
                            )}
                            {switching && !isActive && profile.id === switching && (
                              <RefreshCw className="h-3 w-3 text-gray-400 animate-spin flex-shrink-0" />
                            )}
                          </div>
                          {profile.company?.name && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              <span className="truncate">{profile.company.name}</span>
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}

                  {filteredProfiles.length === 0 && searchQuery && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No accounts found
                    </p>
                  )}
                </div>
              </ScrollArea>
            </div>
          </>
        )}

        {/* Create Company */}
        <Separator />
        <div className="p-2">
          <Link
            to="/create-company"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors w-full"
          >
            <div className="h-9 w-9 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center flex-shrink-0">
              <Plus className="h-4 w-4 text-gray-400" />
            </div>
            <span className="font-medium">Create Company</span>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
};
